import { useMutation, useQuery } from '@apollo/client'
import { useFormik } from 'formik'
import { FollowArtistBulkMutation, FollowArtistBulkMutationVariables } from 'gql'
import { FC } from 'react'
import Placeholder from '~ui/Placeholder'
import { getImageUrl } from '~utils/index'
import { GET_STARS_BY_FOLLOWERS } from 'gql/stars/queries'
import { USER_BASE_DATA } from 'gql/user/queries'
import { FOLLOW_ARTIST_BULK } from 'gql/artist/mutations'
import StarsCard from './star-card'

type FollowStarsContainerType = { onCompleted?: () => void }

const FollowStarsContainer: FC<FollowStarsContainerType> = ({ onCompleted }) => {
  const { data } = useQuery(GET_STARS_BY_FOLLOWERS, { variables: { first: 8 } })

  const [followArtistMutate, { loading }] = useMutation<
    FollowArtistBulkMutation,
    FollowArtistBulkMutationVariables
  >(FOLLOW_ARTIST_BULK, {
    refetchQueries: [USER_BASE_DATA],
  })

  const { values, setFieldValue, handleSubmit } = useFormik({
    initialValues: { followStars: {} },
    onSubmit: async (v) => {
      const artistsToSubscribe = Object.entries(v.followStars)
        .map(([id, checked]) => (checked ? id : undefined))
        .filter(Boolean)

      await followArtistMutate({
        variables: { input: { ids: artistsToSubscribe, withCredit: true } },
      })
      onCompleted?.()
    },
  })

  const artists = data?.allArtists.edges.map(({ node }) => node)

  return (
    <div className="flex flex-col space-y-10 m-auto max-w-screen-xl lg:min-w-screen-2xl w-full">
      <p className="text-center text-lg">Follow 3 stars and get $1 in credit for each.</p>

      <form id="follow-stars" onSubmit={handleSubmit}>
        <div className="grid gap-7 grid-cols-2 lg:grid-cols-4">
          {artists?.length
            ? artists.map(({ id, ...artist }) => (
                <StarsCard
                  key={id}
                  id={id}
                  name="followStars"
                  label={artist.fullName}
                  onChange={(v) => {
                    setFieldValue('followStars', { ...values.followStars, [id]: v })
                  }}
                  img={getImageUrl(artist.file, 300, 200)}
                />
              ))
            : Array(8)
                .fill(0)
                // eslint-disable-next-line react/no-array-index-key
                .map((_, i) => <Placeholder key={i} className="h-[200px]" />)}
        </div>

        <div className="flex mt-10">
          <button
            disabled={loading || !Object.entries(values.followStars).length}
            className="btn btn-primary w-full m-auto lg:w-1/2"
            type="submit"
          >
            {loading ? 'submitting' : 'continue'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default FollowStarsContainer
